import { Link, NavLink } from "react-router-dom";
import type { User } from "../auth/useUser";
import { apiClient } from "../api/client";
import { useAdminAlerts } from "../hooks/useAdminAlerts";
import { ALERTS_HEADING, pausedCountName } from "../lib/adminAlerts";

// Admin top bar. Same structure as the user-app Header (sticky +
// backdrop-blur) but data-dense rather than founder-facing — no
// breadcrumb / credit-balance widget. Logo + nav links + admin
// display name + sign-out.
//
// Top-level destinations the operator should be able to reach from
// any page: Users (the home table), Plans, Audit log. User-detail
// / product-detail / run-detail are reached by drilling in from
// Users; they don't need top-bar entries.
//
// 0064: "Alerts" (right after "Runs") opens the saved passes that wait
// on the admin (views/Alerts.tsx) and carries their count while any is
// open (useAdminAlerts).
const ALERTS_PATH = "/admin/alerts";
const NAV_ITEMS: { to: string; label: string }[] = [
  { to: "/", label: "Users" },
  { to: "/admin/products", label: "Products" },
  { to: "/admin/runs", label: "Runs" },
  { to: ALERTS_PATH, label: ALERTS_HEADING },
  { to: "/metrics", label: "Metrics" },
  { to: "/plans", label: "Plans" },
  { to: "/audit", label: "Audit" },
  { to: "/admin/sub-prompts", label: "Sub-prompts" },
  { to: "/admin/settings", label: "Settings" },
];

export function Header({ user }: { user: User }) {
  const alertsQ = useAdminAlerts(!!user.is_admin);
  const openAlerts = alertsQ.isSuccess ? alertsQ.data.length : 0;
  const onSignOut = async () => {
    await apiClient.POST("/api/v1/logout", {
      params: { header: { "X-Requested-With": "xhr" } },
    });
    window.location.href = "/login";
  };

  return (
    <header className="sticky top-0 z-40 backdrop-blur bg-ink-950/80 border-b border-ink-800">
      <div className="max-w-6xl mx-auto px-4 sm:px-8 h-14 flex items-center gap-6">
        <Link
          to="/"
          className="font-mono text-sm tracking-tightest text-ink-50 hover:text-accent transition-colors"
        >
          eminity<span className="text-ink-400">/admin</span>
        </Link>
        <nav className="flex items-center gap-4 text-sm">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              className={({ isActive }) =>
                `transition-colors ${
                  isActive
                    ? "text-ink-50"
                    : "text-ink-400 hover:text-ink-100"
                }`
              }
            >
              {item.label}
              {item.to === ALERTS_PATH && openAlerts > 0 && (
                <>
                  <span
                    aria-hidden="true"
                    className="ml-1.5 rounded-full bg-amber-500/20 px-1.5 font-mono text-[10px] text-amber-200"
                  >
                    {openAlerts}
                  </span>
                  <span className="sr-only"> ({pausedCountName(openAlerts)})</span>
                </>
              )}
            </NavLink>
          ))}
        </nav>
        <span className="ml-auto text-xs text-ink-400">{user.display_name}</span>
        <button
          type="button"
          onClick={onSignOut}
          className="text-xs text-ink-400 hover:text-ink-100 transition-colors"
        >
          sign out
        </button>
      </div>
    </header>
  );
}
